import React from 'react';
import { useTranslation } from 'react-i18next';

function OptionDeleteModal({ show, onHide, onConfirm, optionName, voteInfo, loading }) {
    const { t } = useTranslation(['modal', 'common']);
    
    if (!show) return null;

    const { hasVotes, voteCount, canDelete, isAdmin } = voteInfo || {};

    const getModalTitle = () => {
        if (hasVotes) {
            return isAdmin ? t('modal:optionDelete.title.withVotes') : t('modal:optionDelete.title.cannotDelete');
        }
        return t('modal:optionDelete.title.default');
    };

    const getAlertClass = () => {
        if (hasVotes && !isAdmin) return 'alert-danger';
        if (hasVotes && isAdmin) return 'alert-warning';
        return 'alert-warning';
    };

    const getAlertIcon = () => {
        if (hasVotes && !isAdmin) return '🚫';
        return '⚠️';
    };

    const getAlertMessage = () => {
        if (hasVotes && !isAdmin) {
            return t('modal:optionDelete.alerts.adminRequired');
        }
        return t('modal:optionDelete.alerts.undoWarning');
    };

    const getBodyMessage = () => {
        if (hasVotes && !isAdmin) {
            return t('modal:optionDelete.messages.nonAdminVotes', { optionName, voteCount });
        }
        if (hasVotes && isAdmin) {
            return t('modal:optionDelete.messages.adminVotes', { optionName, voteCount });
        }
        return t('modal:optionDelete.messages.noVotes', { optionName });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onHide();
        } else if (e.key === 'Enter' && canDelete && !loading) {
            onConfirm();
        }
    };

    return (
        <div 
            className="modal show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onKeyDown={handleKeyDown}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{getModalTitle()}</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onHide}
                            disabled={loading}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className={`alert ${getAlertClass()}`}>
                            <strong>{getAlertIcon()} {getAlertMessage()}</strong>
                        </div>
                        
                        <p>{getBodyMessage()}</p>
                        
                        {hasVotes && (
                            <div className="vote-info bg-light p-3 rounded">
                                <div>
                                    <strong>{t('modal:optionDelete.voteInfo.title')}</strong>
                                </div>
                                <div className="mt-2">
                                    • {t('modal:optionDelete.voteInfo.count', { count: voteCount })}
                                    <br />
                                    • {t('modal:optionDelete.voteInfo.canDelete', { canDelete })}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary" 
                            onClick={onHide}
                            disabled={loading}
                        >
                            {hasVotes && !isAdmin ? t('modal:optionDelete.buttons.close') : t('modal:optionDelete.buttons.cancel')}
                        </button>
                        {canDelete && (
                            <button 
                                type="button" 
                                className="btn btn-danger" 
                                onClick={onConfirm}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        {t('modal:optionDelete.buttons.deleting')}
                                    </>
                                ) : (
                                    hasVotes ? t('modal:optionDelete.buttons.deleteAnyway') : t('modal:optionDelete.buttons.delete')
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OptionDeleteModal;